import { useEffect, useRef } from 'react';

/**
 * useKeyDown
 *
 * Attaches a `keydown` listener to `target` (defaults to `window`) and forwards
 * each event to `handler`. The listener is attached once; the latest `handler`
 * is always invoked via a ref, so an inline handler reading fresh state/props is
 * safe and never goes stale — no dependency array to maintain at the call site.
 *
 * @param {(event: KeyboardEvent) => void} handler - Called on every keydown.
 * @param {{ target?: Window | Document | HTMLElement }} [options]
 *        - target: element to listen on (default `window`).
 *
 * @example
 *   useKeyDown((e) => { if (e.key === 'Escape') close(); });
 */
const useKeyDown = (handler, { target } = {}) => {
  const handlerRef = useRef(handler);
  handlerRef.current = handler;

  useEffect(() => {
    const el = target || window;
    if (!el) return undefined;
    const listener = (event) => handlerRef.current(event);
    el.addEventListener('keydown', listener);
    return () => el.removeEventListener('keydown', listener);
  }, [target]);
};

export default useKeyDown;
